/**
 * The Contacts page: the people behind an agent's calls, the most recently heard from first.
 *
 * THE OWNER'S FILING, NOT THE AGENT'S MEMORY. A call from a number files itself under that
 * number's contact, and a person can name them, add their other numbers, write a note, merge
 * two that turn out to be one person, or delete one. Nothing here is ever read by the agent
 * on a call - see `domain/contacts.ts` in the Worker.
 *
 * A CONTACT IS A CHILD ROUTE, the way a call is: `/contacts/:id` renders the dialog INSIDE
 * this page, so the list, its search and its loaded pages survive opening one and closing it.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, Outlet, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { api, post, useApi, type ContactDetail, type ContactStatus, type ContactSummary } from "../api";
import { PAGE_WIDTH, PageHead } from "../components/Page";
import { Empty, SkeletonRows } from "../components/States";
import { Dialog } from "../components/Dialog";
import { Field } from "../components/Form";
import { useScopedTo } from "../components/Shell";
import { Close, Plus, Search, User } from "../components/Icons";
import { useAgent } from "../currentAgent";
import { Button, IconButton, Segmented, SettingCard, TextInput, cx } from "../ui";
import { phone, when } from "../format";

interface Listed {
  contacts: ContactSummary[];
  cursor: string | null;
}

/** What the page hands the contact dialog underneath it. */
export interface ContactListContext {
  /** Back to the list, search and agent kept. */
  listHref: string;
  /** Fetch the list again after the dialog changed a contact, merged one or deleted one. */
  reload: () => void;
}

export function useContactList(): ContactListContext {
  return useOutletContext<ContactListContext>();
}

/** A status in words. The same four everywhere, in every industry. */
export const STATUS_WORDS: Record<ContactStatus, string> = {
  new: "New",
  lead: "Lead",
  customer: "Customer",
  not_a_fit: "Not a fit",
};

type View = "all" | "waiting" | "lead" | "customer";

function viewOf(params: URLSearchParams): View {
  if (params.get("open") === "true") return "waiting";
  const status = params.get("status");
  return status === "lead" || status === "customer" ? status : "all";
}

/** The list's columns. Under 720px only the name and the number are left. */
const ROW =
  "grid grid-cols-[minmax(0,2fr)_minmax(0,1.3fr)_110px_60px_170px] items-center gap-x-4 px-4 " +
  "max-[720px]:grid-cols-[minmax(0,1fr)_auto]";

/**
 * A form IN the contact dialog's side panel, where a second dialog would otherwise open over
 * the first. The same four parts a dialog has - a title, a way out, a body and a footer of
 * buttons - so a form written for one reads the same in the other.
 */
export function Pane({
  title,
  onClose,
  footer,
  children,
}: {
  title: ReactNode;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
}) {
  return (
    <SettingCard flush>
      <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
        <h3 className="text-sm font-semibold text-ink">{title}</h3>
        <IconButton type="button" title="Close" onClick={onClose}>
          <Close size={16} />
          <span className="sr-only">Close</span>
        </IconButton>
      </div>
      <div className="px-4 py-4">{children}</div>
      {footer && <div className="flex flex-wrap items-center gap-2 border-t border-line px-4 py-3">{footer}</div>}
    </SettingCard>
  );
}

/** The line a contact is known by: their name, else their number, else their email. */
export function contactTitle(contact: Pick<ContactSummary, "name" | "phones" | "emails">): string {
  return contact.name ?? phone(contact.phones[0]) ?? contact.emails[0] ?? "No name";
}

export function Contacts() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const current = useAgent();
  const tz = current.timezone;

  // Debounced apart from the URL, as on /calls, so typing does not push a history entry per
  // keystroke.
  const [search, setSearch] = useState(params.get("q") ?? "");
  useEffect(() => {
    const timer = setTimeout(() => {
      setParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          if (search) next.set("q", search);
          else next.delete("q");
          return next;
        },
        { replace: true },
      );
    }, 250);
    return () => clearTimeout(timer);
  }, [search, setParams]);

  // The search and the filters, and nothing else: the dialog's own path is not part of which
  // list this is.
  const kept = new URLSearchParams();
  for (const key of ["q", "open", "status"]) {
    const value = params.get(key);
    if (value) kept.set(key, value);
  }
  const query = kept.toString();
  const { rows, error, loading, loadingMore, nextCursor, loadMore, reload } = useContactPages(current.id, query);
  const [adding, setAdding] = useState(false);
  const view = viewOf(params);
  const filtered = Boolean(params.get("q")) || view !== "all";
  // ONE SWITCH FOR WHO IS ON THE LIST. Waiting on you, leads and customers are the three
  // questions an owner opens this page with; a status dropdown beside a waiting toggle was
  // two controls for one question.
  const show = (next: View) =>
    setParams((previous) => {
      const search = new URLSearchParams(previous);
      search.delete("open");
      search.delete("status");
      if (next === "waiting") search.set("open", "true");
      if (next === "lead" || next === "customer") search.set("status", next);
      return search;
    });

  const hrefFor = (id: string) => `/${current.slug}/contacts/${id}${query ? `?${query}` : ""}`;
  const context: ContactListContext = { listHref: `/${current.slug}/contacts${query ? `?${query}` : ""}`, reload };

  return (
    <>
      <PageHead
        title="Contacts"
        contain={PAGE_WIDTH}
        actions={
          <Button variant="primary" onClick={() => setAdding(true)}>
            <Plus size={16} /> New contact
          </Button>
        }
        below={
          <div className="flex flex-wrap items-center gap-2">
            <label className="relative flex items-center">
              <Search size={17} className="pointer-events-none absolute left-3 text-ink" />
              <TextInput
                pill
                type="search"
                placeholder="Search"
                aria-label="Search contacts"
                className="min-w-64 pl-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
            <Segmented
              label="Show"
              value={view}
              options={[
                { value: "all", label: "All" },
                { value: "waiting", label: "Waiting on you" },
                { value: "lead", label: "Leads" },
                { value: "customer", label: "Customers" },
              ]}
              onChange={show}
            />
          </div>
        }
      />

      <div className={PAGE_WIDTH}>
        {error && <Empty tone="alert" title="Couldn't load contacts" body={error.message} />}

        {loading && !rows.length && <SkeletonRows rows={8} />}

        {!error && !loading && !rows.length && (
          <Empty
            icon={User}
            title={view === "waiting" && !params.get("q") ? "Nobody is waiting on you" : filtered ? "Nobody here" : "No contacts yet"}
            body={
              filtered
                ? undefined
                : "Everyone who rings your agent from a number gets a contact here, with their calls under it."
            }
          />
        )}

        {rows.length > 0 && (
          <div className="overflow-hidden rounded-lg border border-line bg-paper">
            <div className={cx(ROW, "border-b border-line py-2.5 text-sm font-medium text-ink max-[720px]:hidden")}>
              <span>Name</span>
              <span>Phone</span>
              <span>Status</span>
              <span className="text-right">Calls</span>
              <span className="text-right">Last call</span>
            </div>
            {rows.map((contact, index) => (
              <Link
                key={contact.id}
                to={hrefFor(contact.id)}
                className={cx(ROW, "py-3 text-sm text-ink hover:bg-sunken", index > 0 && "border-t border-line-soft")}
              >
                <span className="flex min-w-0 items-center gap-2">
                  <span className="truncate font-medium">{contactTitle(contact)}</span>
                  {/* WAITING ON YOU IS A DOT, in ink, after the name: the one thing on a row
                      that asks for something, marked without a coloured badge. */}
                  {contact.waiting && (
                    <span className="size-2 flex-none rounded-full bg-ink" title="Waiting on you">
                      <span className="sr-only">Waiting on you</span>
                    </span>
                  )}
                </span>
                <span className="nums truncate">{contact.name ? phone(contact.phones[0]) : null}</span>
                <span className="max-[720px]:hidden">{STATUS_WORDS[contact.status.shown]}</span>
                <span className="nums text-right max-[720px]:hidden">{contact.calls}</span>
                <span className="nums text-right text-ink max-[720px]:hidden">{when(contact.lastSeenAt, tz)}</span>
              </Link>
            ))}
          </div>
        )}

        {nextCursor && (
          <button
            onClick={() => void loadMore()}
            disabled={loadingMore}
            className="mt-4.5 w-full cursor-pointer rounded-sm border border-line bg-paper p-2.75 text-sm font-medium text-ink hover:border-line-strong disabled:opacity-55"
          >
            {loadingMore ? "Loading…" : "Load more contacts"}
          </button>
        )}
      </div>

      {adding && (
        <ContactForm
          title="New contact"
          path={`/v1/contacts?agent=${encodeURIComponent(current.id)}`}
          onClose={() => setAdding(false)}
          onSaved={(saved) => {
            setAdding(false);
            reload();
            navigate(hrefFor(saved.id));
          }}
        />
      )}

      <Outlet context={context} />
    </>
  );
}

/** The pages of contacts behind one query string. `useCallPages`, for people. */
function useContactPages(agentId: string, query: string) {
  const [nonce, setNonce] = useState(0);
  const listed = `/v1/contacts?agent=${encodeURIComponent(agentId)}${query ? `&${query}` : ""}`;
  const { data, error, loading, reload: refetch } = useApi<Listed>(listed);
  const [extra, setExtra] = useState<ContactSummary[]>([]);
  // Undefined until a second page is asked for, for the reason `useCallPages` gives.
  const [cursor, setCursor] = useState<string | null | undefined>(undefined);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    setExtra([]);
    setCursor(undefined);
  }, [listed, nonce]);

  const rows = useMemo(() => (data ? [...data.contacts, ...extra] : extra), [data, extra]);
  const nextCursor = cursor === undefined ? (data?.cursor ?? null) : cursor;

  async function loadMore() {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await api<Listed>(`${listed}&cursor=${encodeURIComponent(nextCursor)}`);
      setExtra((current) => [...current, ...page.contacts]);
      setCursor(page.cursor);
    } finally {
      setLoadingMore(false);
    }
  }

  const reload = useCallback(() => {
    setNonce((n) => n + 1);
    refetch();
  }, [refetch]);

  return { rows, error, loading, loadingMore, nextCursor, loadMore, reload };
}

/**
 * A contact's details, to add one or to edit one. The same form for both, and it saves the
 * whole contact: every number and email as the person left them.
 *
 * ONE FIELD PER NUMBER AND PER EMAIL, each with its own remove and an "Add" under the list.
 * The first number is the one the Call button rings. The Worker reads the country off the
 * business for a local number and says which one it could not read.
 */
export function ContactForm({
  title,
  path,
  initial,
  onClose,
  onSaved,
  onMerge,
  onDelete,
  inline,
}: {
  title: string;
  /** Where the form posts: `/v1/contacts?agent=` to add, `/v1/contacts/:id` to edit. */
  path: string;
  initial?: ContactSummary;
  onClose: () => void;
  onSaved: (contact: ContactDetail) => void;
  /** Editing only: the two things you do to a contact rather than to its details. */
  onMerge?: () => void;
  onDelete?: () => void;
  /** In the contact dialog's side panel rather than as a dialog of its own. */
  inline?: boolean;
}) {
  const [name, setName] = useState(initial?.name ?? "");
  const [company, setCompany] = useState(initial?.company ?? "");
  const [location, setLocation] = useState(initial?.location ?? "");
  // One empty field to start with when there are none, so there is somewhere to type.
  const [phones, setPhones] = useState<string[]>(() => {
    const known = (initial?.phones ?? []).map((p) => phone(p) ?? p);
    return known.length ? known : [""];
  });
  const [emails, setEmails] = useState<string[]>(() => (initial?.emails?.length ? initial.emails : [""]));
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const form = "contact-form";

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const filled = (values: string[]) => values.map((value) => value.trim()).filter(Boolean);
      const { contact } = await post<{ contact: ContactDetail }>(path, {
        name,
        company,
        location,
        phones: filled(phones),
        emails: filled(emails),
      });
      onSaved(contact);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  const Frame = inline ? Pane : Dialog;
  return (
    <Frame
      title={title}
      onClose={onClose}
      footer={
        <>
          {/* The edit dialog's footer, as on Categories: what removes the thing sits on the
              left, away from Save. */}
          {/* Plain words rather than buttons: they are rarer than Save, and a row of four
              bordered buttons does not fit the side panel's width. */}
          {onDelete && (
            <button type="button" className="cursor-pointer px-1 text-sm font-medium text-alert hover:underline" onClick={onDelete}>
              Delete
            </button>
          )}
          {onMerge && (
            <button type="button" className="cursor-pointer px-1 text-sm font-medium text-ink hover:underline" onClick={onMerge}>
              Merge
            </button>
          )}
          <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
            {error && <span className="text-sm text-alert">{error}</span>}
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" form={form} variant="primary" disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </>
      }
    >
      <form id={form} onSubmit={submit} className="grid gap-4">
        <Field label="Name">
          <TextInput className="w-full" autoFocus value={name} maxLength={200} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Company">
          <TextInput className="w-full" value={company} maxLength={200} onChange={(e) => setCompany(e.target.value)} />
        </Field>
        <Field label="Address">
          <TextInput className="w-full" value={location} maxLength={200} onChange={(e) => setLocation(e.target.value)} />
        </Field>
        <ListField label="Phone" type="tel" values={phones} onChange={setPhones} add="Add phone" />
        <ListField label="Email" type="email" values={emails} onChange={setEmails} add="Add email" />
      </form>
    </Frame>
  );
}

/**
 * A short list of one kind of thing - numbers, emails - as one field each. A fieldset rather
 * than `Field`, whose `<label>` wraps exactly one control.
 */
function ListField({
  label,
  type,
  values,
  onChange,
  add,
}: {
  label: string;
  type: "tel" | "email";
  values: string[];
  onChange: (next: string[]) => void;
  add: string;
}) {
  const set = (index: number, value: string) => onChange(values.map((old, i) => (i === index ? value : old)));
  return (
    <fieldset className="grid gap-2">
      <legend className="mb-1.5 text-sm font-medium text-ink">{label}</legend>
      {values.map((value, index) => (
        <div key={index} className="flex items-center gap-2">
          <TextInput
            type={type}
            aria-label={values.length > 1 ? `${label} ${index + 1}` : label}
            className="nums min-w-0 flex-1"
            maxLength={200}
            value={value}
            onChange={(e) => set(index, e.target.value)}
          />
          {values.length > 1 && (
            <IconButton type="button" title={`Remove this ${label.toLowerCase()}`} onClick={() => onChange(values.filter((_, i) => i !== index))}>
              <Close size={14} />
              <span className="sr-only">Remove this {label.toLowerCase()}</span>
            </IconButton>
          )}
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...values, ""])}
        className="inline-flex w-max cursor-pointer items-center gap-1 text-sm font-medium text-ink hover:underline"
      >
        <Plus size={14} /> {add}
      </button>
    </fieldset>
  );
}
